import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PlanCard from '../components/plancard/PlanCard';
import '../pages/Billing.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export default function Pricing() {
  const [cadence, setCadence] = useState('annual');
  const [planPromos, setPlanPromos] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/billing/plan-promos`)
      .then((res) => { if (!cancelled) setPlanPromos(res.data?.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const starterMonthlyPrice = 109;
  const teamMonthlyPrice = 313;
  const proMonthlyPrice = 656;
  const starterAnnualTotal = 997;
  const teamAnnualTotal = 2856;
  const proAnnualTotal = 5868;
  const starterMonthlyEquivalent = Math.round(starterAnnualTotal / 12);
  const teamMonthlyEquivalent = Math.round(teamAnnualTotal / 12);
  const proMonthlyEquivalent = Math.round(proAnnualTotal / 12);
  const starterSavingPct = Math.round((1 - starterAnnualTotal / (starterMonthlyPrice * 12)) * 100);

  const starterPricePrimary = cadence === 'annual' ? `$${starterMonthlyEquivalent}` : `$${starterMonthlyPrice}`;
  const starterPriceStrikethrough = cadence === 'annual' ? `$${starterMonthlyPrice}` : null;
  const starterPriceSecondary = cadence === 'annual' ? `per month ($${starterAnnualTotal.toLocaleString()}/p.a.)` : 'per month';
  const teamPricePrimary = cadence === 'annual' ? `$${teamMonthlyEquivalent}` : `$${teamMonthlyPrice}`;
  const teamPriceStrikethrough = cadence === 'annual' ? `$${teamMonthlyPrice}` : null;
  const teamPriceSecondary = cadence === 'annual' ? `per month ($${teamAnnualTotal.toLocaleString()}/p.a.)` : 'per month';
  const proPricePrimary = cadence === 'annual' ? `$${proMonthlyEquivalent}` : `$${proMonthlyPrice}`;
  const proPriceStrikethrough = cadence === 'annual' ? `$${proMonthlyPrice}` : null;
  const proPriceSecondary = cadence === 'annual' ? `per month ($${proAnnualTotal.toLocaleString()}/p.a.)` : 'per month';

  return (
    <div className="billing-shell">
      <div className="billing-hero py-4 px-3">
        <div className="container-lg">
          <a href="https://tryzilch.com" className="navbar-brand d-flex align-items-center gap-2 mb-4" style={{ textDecoration: 'none', color: 'inherit', width: 'fit-content' }}>
            <img
              src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
              width="32"
              height="32"
              alt="Zilch Logo"
            />
            <span style={{ fontWeight: 700, fontSize: 18 }}>Zilch.</span>
          </a>
          <h1 className="billing-title mb-2" style={{ fontSize: 'clamp(24px, 4vw, 36px)' }}>Simple, transparent pricing</h1>
          <p className="billing-subtitle mb-3">Start with a 7-day free trial. Cancel anytime before it ends.</p>
          <div className="d-flex align-items-center gap-2">
            <span className="billing-subtitle" style={{ fontSize: 13 }}>Monthly</span>
            <button
              role="switch"
              aria-label="Billing cadence"
              aria-checked={cadence === 'annual'}
              className={`billing-toggle__switch ${cadence === 'annual' ? 'is-active' : ''}`}
              onClick={() => setCadence(cadence === 'annual' ? 'monthly' : 'annual')}
            />
            <span className="billing-subtitle" style={{ fontSize: 13 }}>
              Annual <span className="badge rounded-pill text-bg-success ms-1">Save {starterSavingPct}%</span>
            </span>
          </div>
        </div>
      </div>

      <div className="container-lg py-4 px-3">
        <div className="row g-3">
          <div className="col-12 col-md-4">
            <PlanCard
              title="Starter"
              subtitle="Launch a sales-ready eco-catalogue"
              pricePrimary={starterPricePrimary}
              priceStrikethrough={starterPriceStrikethrough}
              priceSecondary={starterPriceSecondary}
              bullets={[
                '100 custom product assessments',
                'One-click product assessments',
                'Bulk product upload (CSV)',
                'Send custom reports to customers',
                'Widget on one site',
                'Product footprint reports & methodology',
                'Public credentials page',
                ...(planPromos?.starter_mcp_promo ? [planPromos.starter_mcp_bullet] : []),
                'Email & chat support',
                { text: 'Team seats', included: false },
                { text: 'Partner API', included: false },
              ]}
              cta="Start 7-day free trial"
              onClick={() => navigate('/signup')}
              footnote="7 days free, then billed monthly. Cancel anytime."
            />
          </div>

          <div className="col-12 col-md-4">
            <PlanCard
              title="Team"
              subtitle="For teams selling on sustainability"
              pricePrimary={teamPricePrimary}
              priceStrikethrough={teamPriceStrikethrough}
              priceSecondary={teamPriceSecondary}
              bullets={[
                'Everything in Starter',
                '500 custom product assessments',
                'Custom widget on 2 sites',
                'AI Agent mode',
                'Customised & co-branded product footprint reports',
                'Personal account manager',
                '2 team seats',
                'Partner API',
              ]}
              cta="Start 7-day free trial"
              onClick={() => navigate('/signup')}
              featured={true}
              footnote="7 days free, then billed monthly. Cancel anytime."
            />
          </div>

          <div className="col-12 col-md-4">
            <PlanCard
              title="Professional"
              subtitle="For scale and transparency"
              pricePrimary={proPricePrimary}
              priceStrikethrough={proPriceStrikethrough}
              priceSecondary={proPriceSecondary}
              bullets={[
                'Everything in Team',
                '1500 custom product assessments',
                'Custom widget on 3 sites',
                'Personal account manager',
                'Manufacturer portal',
                '3 team seats',
                'Partner API',
              ]}
              cta="Start 7-day free trial"
              onClick={() => navigate('/signup')}
              footnote="7 days free, then billed monthly. Cancel anytime."
            />
          </div>
        </div>

        <div className="row g-3 mt-2">
          <div className="col-12 col-md-6">
            <PlanCard
              title="Enterprise & Custom"
              subtitle="For large teams with bespoke needs"
              bullets={[
                'Unlimited SKUs',
                'Multi-brand sites',
                'Unlimited sites',
                'Custom integrations',
              ]}
              cta="Contact Zilch"
              onClick={() => window.open('https://tryzilch.com/contact')}
            />
          </div>
          <div className="col-12 col-md-6">
            <PlanCard
              title="Services"
              subtitle="Hands-on sustainability support"
              bullets={[
                'Product assessments',
                'Circular economy initiatives',
                'Offsetting support',
                'Company-level emissions',
              ]}
              cta="Contact Zilch"
              onClick={() => window.open('https://tryzilch.com/contact')}
            />
          </div>
        </div>

        <div className="row g-3 mt-2">
          <div className="col-12">
            <PlanCard
              title="Need help choosing?"
              subtitle="Not sure which plan fits"
              bullets={[
                'Most teams start with Starter’s free trial',
                'Switch between plans at any time',
                'Cancel before your trial ends, no charge',
                'We’ll help you pick the right fit',
              ]}
              cta="Talk to us"
              onClick={() => window.open('https://tryzilch.com/contact')}
            />
          </div>
        </div>

        <p className="text-center text-muted mt-4" style={{ fontSize: 12 }}>
          Payments processed securely by <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">Stripe</a>.
        </p>
      </div>
    </div>
  );
}
